// @ts-strict-ignore
import * as React from 'react';
import {createPortal} from 'react-dom';
import {clsx} from 'clsx';
import range from 'lodash/range';
import {FloatingOverlay} from '@floating-ui/react';

import {DEFAULT_CHART_HEIGHT, ProjectFeatureFlag} from 'common/report/constants';
import {withStyles} from 'common/util/react';
import {ItemClickEvent, Section, SectionItem, TooltipInfo} from 'common/components/items-menu/types';
import {useSegmentFilterController} from 'common/contexts/react/segment-filter-controller-context';
import {useTranslation} from 'common/i18n/hooks';
import {SegmentColorPicker} from 'common/query-builder/chart-customization/colors/segment-color-picker';
import {useFeatureFlag} from 'common/hooks/useFeatureFlag';

import {TooltipAttrs} from '../tooltip/types';
import {InputReact} from '../input';
import {ChartTooltip} from '../chart-tooltip';
import {Directions, PositionParentType} from '../drop-menu/types';
import {ItemsMenuReact} from '../items-menu';
import {ButtonReact} from '../button';
import {ButtonSize, ButtonTheme} from '../button/constants';
import {ChartLegendTheme} from './constants';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import {rollUpExtraItemsIntoOther} from './utils';

export interface ChartLegendProps {
  className?: string;
  backgroundColor?: string;
  enableSearch?: boolean;
  sections?: Section[];
  segments?: string[];
  showSelectAll?: boolean;
  height?: string;
  wrapLabels?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  maxLegendItems?: number;
  maxTableItemWidth?: string;
  theme?: ChartLegendTheme;
  selectAllDisabled?: boolean;
  selectAllTooltip?: TooltipInfo;
  tooltip?: TooltipAttrs;
  onToggleCheckbox?: (toggle: {
    hiddenSeries: string[];
    isSelectAll: boolean;
    item?: SectionItem<{
      sectionIdx: number;
      segmentId: string;
    }>;
  }) => void;
  /** whether legend items should be left-justified (used by metric charts when main metric is left justified) */
  justifyStart?: boolean;
}

const SEARCH_HEIGHT = 56;

const ChartLegend = React.forwardRef<HTMLDivElement, ChartLegendProps>(function (props, ref) {
  const hasConsistentColorsFeatureFlag = useFeatureFlag(ProjectFeatureFlag.ConsistentColors);
  const {print} = useTranslation();
  const segmentFilterController = useSegmentFilterController();

  const inputRef = React.useRef(null);

  const itemsRefs = React.useRef([]);

  const [hiddenSeries, setHiddenSeries] = React.useState<string[]>([]);
  const [searchQuery, setSearchQuery] = React.useState(``);
  const [showChartTooltip, setShowChartTooltip] = React.useState(false);
  const [activeLegendItemIndex, setActiveLegendItemIndex] = React.useState<number | null>(null);

  const handleToggledAllCheckboxes = () => {
    const newHiddenSeries = hiddenSeries.length > 0 ? [] : props.segments ?? [];
    setHiddenSeries(newHiddenSeries);
    if (props.onToggleCheckbox) {
      props.onToggleCheckbox({
        hiddenSeries: newHiddenSeries,
        isSelectAll: true,
      });
    }
  };

  const handleToggledCheckbox = (ev: ItemClickEvent) => {
    const item = ev.detail.item;
    const originalHiddenSeries = hiddenSeries;
    let newHiddenSeries = originalHiddenSeries;
    if (originalHiddenSeries.includes(print(item.label))) {
      newHiddenSeries = originalHiddenSeries.filter((value) => value !== item.label);
    } else {
      newHiddenSeries.push(print(item.label));
    }
    setHiddenSeries(newHiddenSeries);
    if (props.onToggleCheckbox) {
      props.onToggleCheckbox({
        item,
        hiddenSeries: newHiddenSeries,
        isSelectAll: false,
      });
    }
  };

  const handleUpdateLegendSearch = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(ev.target.value.toLowerCase());
  };

  const getItemsMenuHeight = (): string => {
    const fromTop = props.enableSearch ? SEARCH_HEIGHT : 0;
    const height = props.height || DEFAULT_CHART_HEIGHT;
    return `calc(${height} - ${fromTop}px)`;
  };

  function LegendLoader() {
    return (
      <div className='loader'>
        {range(9).map((_, idx) => (
          <div className='mp-skeleton-large' key={idx} />
        ))}
      </div>
    );
  }

  const onSegmentMouseOverChange = (item: SectionItem, mouseOver: boolean) => {
    // displays the overflow legend items
    if (item.tooltipSections) {
      setShowChartTooltip(mouseOver);
    } else {
      segmentFilterController.notifySeriesHovered(mouseOver ? {seriesId: item.value, source: `legend`} : null);
    }
  };

  const onLegendItemClick = (ev: MouseEvent, itemIndex: number) => {
    if (!hasConsistentColorsFeatureFlag) {
      return;
    }
    ev.stopPropagation();
    setActiveLegendItemIndex(itemIndex);
  };

  // Only allow one section for card legends
  let themedLegend = null;
  if (props.theme === ChartLegendTheme.CardTable) {
    let {items} = props.sections[0];

    items = rollUpExtraItemsIntoOther(items, props.maxLegendItems);
    themedLegend = (
      <div className={clsx(styles.legendContainer)}>
        <div className={clsx(styles.legendLine, {[styles.justifyStart]: props.justifyStart})}>
          {items.map((item, itemIndex) => {
            const labelString = print(item.label);
            const canClickItem = hasConsistentColorsFeatureFlag && !item.tooltipSections;

            return (
              <ButtonReact
                key={labelString}
                ref={(el) => (itemsRefs.current[itemIndex] = el)}
                pressed={activeLegendItemIndex === itemIndex}
                className={clsx(styles.legendSegment, canClickItem && styles.clickable, `truncated`)}
                style={{maxWidth: props.maxTableItemWidth ?? ``}}
                theme={ButtonTheme.Tertiary}
                size={ButtonSize.Inline}
                onMouseEnter={() => onSegmentMouseOverChange(item, true)}
                onMouseLeave={() => onSegmentMouseOverChange(item, false)}
                onClick={canClickItem && ((ev) => onLegendItemClick(ev, itemIndex))}
              >
                {item.label ? (
                  <div className={styles.segmentText} style={{'--color': item.color}}>
                    {labelString}
                  </div>
                ) : null}

                {item.tooltipSections && showChartTooltip ? (
                  item.tooltipSections?.chartSection ? (
                    <ChartTooltip
                      sections={item.tooltipSections.chartSection}
                      titleColor={item.tooltipSections.color}
                      direction={Directions.Down}
                      positioning={{positionParent: PositionParentType.ParentElement}}
                    />
                  ) : (
                    // renders an overflow dropdown of the remaining legend items
                    <ChartTooltip positioning={{positionParent: PositionParentType.ParentElement}}>
                      <div>
                        {item.tooltipSections.map(({color, label, tooltipSections}, index) => (
                          <div className={clsx(styles.segmentText)} key={index} style={{'--color': color}}>
                            {label}
                            {tooltipSections?.chartSection ? (
                              <ChartTooltip
                                direction={Directions.Right}
                                positioning={{positionParent: PositionParentType.ParentElement}}
                                sections={tooltipSections?.chartSection}
                                titleColor={color}
                              />
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </ChartTooltip>
                  )
                ) : null}
              </ButtonReact>
            );
          })}

          {/* renders color override dropdown */}
          {activeLegendItemIndex !== null &&
            createPortal(
              <>
                <FloatingOverlay>
                  <SegmentColorPicker
                    path={items[activeLegendItemIndex].value ?? items[activeLegendItemIndex].label}
                    segmentColor={items[activeLegendItemIndex].color}
                    positionParent={itemsRefs.current[activeLegendItemIndex]}
                    onClose={() => setActiveLegendItemIndex(null)}
                  />
                </FloatingOverlay>
              </>,
              window.document.querySelector(`#modals`),
            )}
        </div>
      </div>
    );
  } else {
    themedLegend = (
      <div className={clsx(styles.container)}>
        <ItemsMenuReact
          style={{maxHeight: getItemsMenuHeight()}}
          elref={`itemsMenu`}
          multiple
          searchFilter={searchQuery}
          showSelectAll={props.showSelectAll}
          selectAllDisabled={props.selectAllDisabled}
          selectAllTooltip={props.selectAllTooltip}
          onHoverItem={(item: SectionItem) => {
            if (item.value?.sectionIdx === 0) {
              segmentFilterController.notifySeriesHovered({
                seriesId: item.label as string,
              });
            } else {
              segmentFilterController.notifySeriesHovered(null);
            }
          }}
          onHoverOut={() => {
            segmentFilterController.notifySeriesHovered(null);
          }}
          sections={props.sections}
          onClickItem={handleToggledCheckbox}
          onSelectAll={handleToggledAllCheckboxes}
          keydownProxyEl={inputRef.current}
        />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      elref='wrapper'
      className={clsx(
        props.theme === ChartLegendTheme.CardTable ? styles.chartThemeTable : styles.chartThemePrimary,
        props.className,
      )}
    >
      {props.theme === ChartLegendTheme.Primary && props.enableSearch ? (
        <div className={clsx(styles.search)}>
          <InputReact
            ref={inputRef}
            icon='search'
            placeholder='Search'
            elref={`search`}
            onInput={handleUpdateLegendSearch}
          />
        </div>
      ) : null}

      {props.loading ? <LegendLoader /> : themedLegend}
    </div>
  );
});

export const ChartLegendReact = withStyles(ChartLegend, css);
